<template>
  <div class="e-custom-select">
    <div
      v-click-outside="() => closeMenu()"
      class="e-custom-select__holder"
      @click="openCloseMenu">
      <div
        v-if="!optionSelected"
        class="e-custom-select__btn"
        :class="openedSelection">
        <div class="e-custom-select__btn__txt">{{ placeholder }}</div>
        <i :class="openedIcon"></i>
      </div>
      <div
        v-if="optionSelected"
        class="e-custom-select__btn e-custom-select__btn--selected">
        <div class="e-custom-select__btn__container">
          <div v-if="!modelValue" class="e-custom-select__btn__label">
            {{ placeholder }}
          </div>
          <div class="e-custom-select__btn__movie">
            <slot name="selected" :selected-option="modelValue"></slot>
          </div>
        </div>
        <i class="icon-close" @click="clearSelected"></i>
      </div>
      <div v-if="opened" ref="dropdown" class="e-custom-select__dropwdown">
        <template v-if="groupBy">
          <div
            v-for="group in groups"
            :key="group"
            class="e-custom-select__group">
            <div class="e-custom-select__group__title">{{ group }}</div>
            <template
              v-for="item in options.filter((i) => i[groupBy] === group)"
              :key="item.id">
              <button
                type="button"
                class="e-custom-select__items"
                :disabled="isDisabledCinema(item)"
                @click="chooseOption(item)">
                <slot
                  name="image"
                  :option="item"
                  class="e-custom-select__items__img"></slot>
                <div class="e-custom-select__items__titles">
                  <div v-if="item.transl" class="e-label e-label--translated">
                    {{ dictionary.translated }}
                  </div>
                  <div class="e-custom-select__items__title">
                    <slot name="title" :option="item"></slot>
                  </div>
                  <div class="e-custom-select__items__subtitle">
                    <slot name="subtitle" :option="item"></slot>
                  </div>
                </div>
              </button>
            </template>
          </div>
        </template>
        <template v-for="item in options" v-else :key="item.id">
          <button
            type="button"
            class="e-custom-select__items"
            :disabled="isDisabledCinema(item)"
            @click="chooseOption(item)">
            <slot
              name="image"
              :option="item"
              class="e-custom-select__items__img"></slot>
            <div class="e-custom-select__items__titles">
              <div v-if="item.transl" class="e-label e-label--translated">
                {{ dictionary.translated }}
              </div>
              <div class="e-custom-select__items__title">
                <slot name="title" :option="item"></slot>
              </div>
              <div class="e-custom-select__items__subtitle">
                <slot name="subtitle" :option="item"></slot>
              </div>
            </div>
          </button>
        </template>
      </div>
    </div>
    <ModalOverlay v-model="showMobileFiltersModal">
      <template #header>
        <i :class="iconClassName"></i>
        <div class="b-pop-up__header__title">{{ placeholder }}</div>
      </template>
      <template #body>
        <div class="b-pop-up__content">
          <div class="e-custom-select__dropwdown">
            <template v-if="groupBy">
              <div
                v-for="group in groups"
                :key="group"
                class="e-custom-select__group">
                <div class="e-custom-select__group__title">{{ group }}</div>
                <template
                  v-for="item in options.filter((i) => i[groupBy] === group)"
                  :key="item.id">
                  <button
                    type="button"
                    class="e-custom-select__items"
                    :disabled="isDisabledCinema(item)"
                    @click="chooseOption(item)">
                    <slot
                      name="image"
                      :option="item"
                      class="e-custom-select__items__img"></slot>
                    <div class="e-custom-select__items__titles">
                      <div
                        v-if="item.transl"
                        class="e-label e-label--translated">
                        {{ dictionary.translated }}
                      </div>
                      <div class="e-custom-select__items__title">
                        <slot name="title" :option="item"></slot>
                      </div>
                      <div class="e-custom-select__items__subtitle">
                        <slot name="subtitle" :option="item"></slot>
                      </div>
                    </div>
                  </button>
                </template>
              </div>
            </template>
            <template v-for="item in options" v-else :key="item.id">
              <button
                type="button"
                class="e-custom-select__items"
                :disabled="isDisabledCinema(item)"
                @click="chooseOption(item)">
                <slot
                  name="image"
                  :option="item"
                  class="e-custom-select__items__img"></slot>
                <div class="e-custom-select__items__titles">
                  <div v-if="item.transl" class="e-label e-label--translated">
                    {{ dictionary.translated }}
                  </div>
                  <div class="e-custom-select__items__title">
                    <slot name="title" :option="item"></slot>
                  </div>
                  <div class="e-custom-select__items__subtitle">
                    <slot name="subtitle" :option="item"></slot>
                  </div>
                </div>
              </button>
            </template>
          </div>
        </div>
      </template>
    </ModalOverlay>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from 'vue'

import useViewport from '../../../features/viewport.js'
import ModalOverlay from '../../components/ModalOverlay.vue'
const { desktop } = useViewport()
const { dictionary } = getCurrentInstance().appContext.config.globalProperties

const showMobileFiltersModal = ref(false)
// props
const props = defineProps({
  type: { default: '', type: String },
  placeholder: { default: 'select item', type: String },
  observerClass: { default: '', type: String },
  options: {
    default() {
      return []
    },
    type: Array,
  },
  modelValue: {
    default() {
      return {}
    },
    type: Object,
  },
  groupBy: {
    default: '',
    type: String,
  },
})

// emits
const emit = defineEmits(['update:modelValue', 'change'])

// reactive data
const opened = ref(false)
const dropdown = ref(null)
const observer = ref(null)

// computed
const groups = computed(() => {
  if (!props.groupBy) {
    return []
  }

  return [...new Set(props.options.map((item) => item[props.groupBy]))]
})

// hooks
onMounted(() => {
  if (props.observerClass) {
    initObserver()
  }
})

const iconClassName = computed(() => {
  return props.type === 'movie' ? 'icon-camera' : 'icon-village'
})

const openedIcon = computed(() => {
  return opened.value ? 'icon-close' : 'icon-arrow-right-up'
})

const openedSelection = computed(() => {
  return opened.value ? 'e-custom-select__btn--open' : ''
})

const optionSelected = computed(() => {
  return Object.keys(props.modelValue).length > 0
})

// methods

const isDisabledCinema = (item) => {
  return props.type === 'cinema' && !item.isOnline
}

const initObserver = () => {
  const observerOptions = {
    root: null,
    threshold: 0.3,
    rootMargins: '-104px 0px 0px 0px',
  }
  observer.value = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        if (dropdown.value) {
          const shiftPixels = dropdown.value.offsetHeight
          dropdown.value.style.top = `-${shiftPixels}px`
        }
      } else {
        dropdown.value.style.top = 'unset'
      }
    })
  }, observerOptions)
}

const openCloseMenu = () => {
  if (desktop.value) {
    opened.value = !opened.value
    if (opened.value && props.observerClass) {
      observeEl()
    } else if (!opened.value) {
      unobserveEl()
    }
  } else {
    showMobileFiltersModal.value = true
  }
}

const closeMenu = async () => {
  opened.value = false

  if (props.observerClass) {
    unobserveEl()
  }
}

const chooseOption = (option) => {
  emit('update:modelValue', option)
  emit('change', option)
  showMobileFiltersModal.value = false
}

const clearSelected = () => {
  emit('update:modelValue', {})
  emit('change', {})
}

// const openMobileModal = () => {
//   if (!desktop) {
//     showMobileFiltersModal.value = true
//   }
// }

const observeEl = async () => {
  const el = document.getElementsByClassName(props.observerClass)[0]
  if (el) {
    observer.value.observe(el)
  }
}
const unobserveEl = async () => {
  const el = document.getElementsByClassName(props.observerClass)[0]
  if (el) {
    observer.value.unobserve(el)
  }
}
</script>
